import type { CrucibleClient } from '@volley/crucible-client';
import type { SoulIntakePlanReviewStatus, SoulIntakeTurnView } from '@volley/crucible-ipc-types';
import { isNoRecoverableIntakeCheckpoint } from './interview-recovery';

export type AdaptiveIntakeMountProbeResult =
  | { readonly state: 'inactive' }
  | { readonly state: 'active'; readonly turn: SoulIntakeTurnView }
  | { readonly state: 'missing-expected-authority' };

const STATUS_PROBE_RETRY_DELAY_MS = 100;

async function waitBeforeStatusProbeRetry(): Promise<void> {
  await new Promise<void>((resolve) => setTimeout(resolve, STATUS_PROBE_RETRY_DELAY_MS));
}

export async function probeAdaptiveIntakeOnWorkspaceMount(input: {
  readonly client: CrucibleClient;
  readonly gameId: string;
  readonly explicitCreateHandoff: boolean;
  readonly shouldStopProbing?: () => boolean;
}): Promise<AdaptiveIntakeMountProbeResult> {
  const shouldStopProbing = input.shouldStopProbing ?? (() => false);
  let status: SoulIntakePlanReviewStatus;
  for (let attempt = 0; ; attempt += 1) {
    try {
      status = await input.client.invoke('intake:plan-review-status', { gameId: input.gameId });
      break;
    } catch {
      if (shouldStopProbing()) return { state: 'inactive' };
      if (attempt === 0) {
        await waitBeforeStatusProbeRetry();
        if (shouldStopProbing()) return { state: 'inactive' };
        continue;
      }
      if (!input.explicitCreateHandoff) return { state: 'inactive' };
      // An explicit create handoff may still be paired with an older runtime
      // that does not expose checkpointPresent (or the status method itself).
      status = { state: 'not-applicable' };
      break;
    }
  }

  const shouldProbe =
    status.checkpointPresent === true ||
    (status.checkpointPresent === undefined && input.explicitCreateHandoff);
  if (!shouldProbe || shouldStopProbing()) return { state: 'inactive' };

  try {
    const turn = await input.client.invoke('intake:turn', { gameId: input.gameId });
    return { state: 'active', turn };
  } catch {
    if (shouldStopProbing()) return { state: 'inactive' };
  }

  try {
    const turn = await input.client.invoke('intake:recover', { gameId: input.gameId });
    return { state: 'active', turn };
  } catch (error) {
    if (!isNoRecoverableIntakeCheckpoint(error)) throw error;
    if (shouldStopProbing() || !input.explicitCreateHandoff) return { state: 'inactive' };
    return { state: 'missing-expected-authority' };
  }
}
